import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import { MuiContainer } from '@/features/shared/material-ui';
import { animationDuration, InsetTop } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, useMemo, useRef } from 'react';
import { Fade } from 'react-reveal';
import { RichContentProps } from './RichContent.interface';
import useStyles from './RichContent.styles';

const mapVariantToProps =
  ({ containerProps }: any) =>
  ({ variant }: any) => {
    if (/^formPicker$/.test(variant)) {
      return {
        containerColor: containerProps.topBackgroundColor,
      };
    }
    return {};
  };

const mapVariantToClassName =
  (classes: any) =>
  ({ variant, props }: any) => {
    if (/column/i.test(variant)) {
      return clsx(
        classes.columnContent,
        /bottom/.test(props.verticalAlignment) && classes.columnBottomAlign,
        /center/.test(props.verticalAlignment) && classes.columnCenterAlign,
      );
    }

    if (/^link/i.test(variant)) {
      return classes.link;
    }

    if (/^multiLink/i.test(variant)) {
      return classes.multiLink;
    }

    if (/rtc/i.test(variant)) {
      return classes.rtc;
    }

    return '';
  };

const mapFirstComponentToContainerProps = ({ variant, ...props }: any) => {
  if (/^video$/.test(variant)) {
    return {
      insetTop: InsetTop.video,
    };
  }

  if (/^quote$/.test(variant)) {
    const isVertical = /^vertical$/i.test(props.orientation);

    return {
      insetTop: isVertical ? InsetTop.quote : undefined,
    };
  }

  return {};
};

const motionPreferenceReduced = () => {
  const mediaQueryList = window.matchMedia('(prefers-reduced-motion: no-preference');
  const prefersReducedMotion = !mediaQueryList.matches;
  return prefersReducedMotion;
};

const RichContent = ({
  detailContent,
  theme,
  contentButtons,
  containerTopBackgroundColor,
  containerBottomBackgroundColor,
  containerInsetWidth,
  topSpacing,
  bottomSpacing,
  includeHorizontalDivider = false,
  backgroundImage,
  mobileBackgroundImage,
}: Br.PropsWithContentButtons<RichContentProps>) => {
  const classes = useStyles({});

  const prefersReduced = useRef(motionPreferenceReduced());

  const containerProps = useMemo(
    () => ({
      topSpacing,
      bottomSpacing,
      topBackgroundColor: containerTopBackgroundColor,
      bottomBackgroundColor: containerBottomBackgroundColor,
      backgroundImage,
      mobileBackgroundImage,
      ...mapFirstComponentToContainerProps(detailContent ? detailContent[0] || {} : {}),
    }),
    [
      bottomSpacing,
      containerBottomBackgroundColor,
      containerTopBackgroundColor,
      detailContent,
      topSpacing,
      backgroundImage,
      mobileBackgroundImage,
    ],
  );

  const rootClassName = clsx(
    classes.root,
    `bottom-${bottomSpacing}`,
    `${theme}-theme`,
    `aos-${prefersReduced.current}`,
  );

  return (
    <ContentContainer {...containerProps}>
      <MuiContainer className={containerInsetWidth}>
        <section className={rootClassName}>
          {contentButtons?.content}
          <Fade duration={animationDuration.primary}>
            <div className={classes.content}>
              <DetailContent
                theme={theme}
                content={detailContent}
                containerProps={containerProps}
                mapVariantToProps={mapVariantToProps({ containerProps })}
                mapVariantToClassName={mapVariantToClassName(classes)}
              />
            </div>
          </Fade>
        </section>
        {includeHorizontalDivider && <div className={classes.horizontalDivider} />}
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(RichContent);
